/**
 * useAutoSave — Reusable auto-save hook for EASI 2.0
 *
 * Provides:
 * - Periodic auto-save when data has changed (dirty tracking)
 * - Global registry for save-before-timeout (HIPAA compliance)
 * - beforeunload warning when unsaved changes exist
 * - Debounced saves to avoid overwhelming the backend
 *
 * IMPORTANT: All saves go to DynamoDB via API — no PHI in browser storage.
 */

import { useEffect, useRef, useCallback, useState } from 'react';

// ============================================================================
// Auto-Save Registry — Global singleton
// ============================================================================

type SaveFunction = () => Promise<void>;

class AutoSaveRegistryClass {
  private saves = new Map<string, SaveFunction>();
  private dirtyFlags = new Map<string, boolean>();

  register(id: string, save: SaveFunction): void {
    this.saves.set(id, save);
  }

  unregister(id: string): void {
    this.saves.delete(id);
    this.dirtyFlags.delete(id);
  }

  markDirty(id: string, dirty: boolean): void {
    this.dirtyFlags.set(id, dirty);
  }

  /** Flush all dirty auto-saves. Called before HIPAA timeout redirect. */
  async flushAll(): Promise<void> {
    const pending: Promise<void>[] = [];
    for (const [id, save] of this.saves) {
      if (this.dirtyFlags.get(id)) {
        pending.push(
          save().catch(() => {
            // Fire-and-forget on timeout — don't block redirect
          })
        );
      }
    }
    // Wait up to 3 seconds for saves, then give up
    if (pending.length > 0) {
      await Promise.race([
        Promise.allSettled(pending),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    }
  }

  hasDirtySaves(): boolean {
    for (const dirty of this.dirtyFlags.values()) {
      if (dirty) return true;
    }
    return false;
  }
}

export const autoSaveRegistry = new AutoSaveRegistryClass();

// ============================================================================
// useAutoSave Hook
// ============================================================================

interface UseAutoSaveOptions<T> {
  /** Current data to track for changes */
  data: T;
  /** Function to save data to the backend */
  saveFunction: (data: T) => Promise<void>;
  /** Interval in ms between auto-saves (default: 30000 = 30s) */
  intervalMs?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
  /** Unique key for this auto-save instance in the global registry */
  key: string;
}

interface UseAutoSaveReturn {
  /** Timestamp of last successful save */
  lastSaved: Date | null;
  /** Whether a save is currently in progress */
  saving: boolean;
  /** Error message from last failed save */
  error: string | null;
  /** Trigger an immediate save */
  saveNow: () => Promise<void>;
  /** Whether there are unsaved changes */
  isDirty: boolean;
}

export function useAutoSave<T>({
  data,
  saveFunction,
  intervalMs = 30000,
  enabled = true,
  key,
}: UseAutoSaveOptions<T>): UseAutoSaveReturn {
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const dataRef = useRef<T>(data);
  const lastSavedDataRef = useRef<string>('');
  const saveFunctionRef = useRef(saveFunction);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);

  // Keep refs current
  saveFunctionRef.current = saveFunction;
  dataRef.current = data;
  // Refs so the flush-on-unmount cleanup (empty-deps effect) reads the
  // latest enabled/key values without re-running on every change.
  const enabledRef = useRef(enabled);
  const keyRef = useRef(key);
  enabledRef.current = enabled;
  keyRef.current = key;

  // Track dirty state by comparing serialized data
  useEffect(() => {
    if (!enabled) return;
    const serialized = JSON.stringify(data);
    const dirty = serialized !== lastSavedDataRef.current;
    setIsDirty(dirty);
    autoSaveRegistry.markDirty(key, dirty);
  }, [data, enabled, key]);

  // Core save function
  const doSave = useCallback(async () => {
    if (savingRef.current) return;
    const currentData = dataRef.current;
    const serialized = JSON.stringify(currentData);
    if (serialized === lastSavedDataRef.current) return;

    savingRef.current = true;
    if (mountedRef.current) setSaving(true);

    try {
      await saveFunctionRef.current(currentData);
      lastSavedDataRef.current = serialized;
      if (mountedRef.current) {
        setLastSaved(new Date());
        setError(null);
        setIsDirty(false);
        autoSaveRegistry.markDirty(key, false);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setError(err?.message || 'Auto-save failed');
      }
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  }, [key]);

  // Register with global registry
  useEffect(() => {
    if (!enabled) return;
    autoSaveRegistry.register(key, doSave);
    return () => {
      autoSaveRegistry.unregister(key);
    };
  }, [key, doSave, enabled]);

  // Flush-on-unmount: when the component unmounts mid-edit (back nav,
  // route change, anything React-internal), fire any pending save to the
  // backend. Without this, edits made within the autosave interval are
  // lost — see Maribeth incident 2026-05-08 and STATE_PRESERVATION_RULES.md
  // rule 3. Empty deps so the cleanup runs only on actual unmount, not on
  // every dep change. Bypasses doSave's state-update side effects (the
  // component is unmounting; setState is wasted) and goes straight to the
  // network call. Fire-and-forget — cleanups cannot await.
  useEffect(() => {
    return () => {
      if (!enabledRef.current) return;
      if (savingRef.current) return;
      const currentData = dataRef.current;
      const serialized = JSON.stringify(currentData);
      if (serialized === lastSavedDataRef.current) return;
      lastSavedDataRef.current = serialized;
      autoSaveRegistry.markDirty(keyRef.current, false);
      void saveFunctionRef.current(currentData).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic auto-save interval
  useEffect(() => {
    if (!enabled) return;
    const intervalId = setInterval(() => {
      if (autoSaveRegistry.hasDirtySaves()) {
        doSave();
      }
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [intervalMs, enabled, doSave]);

  // Mark last saved data on first render (so initial load isn't dirty)
  useEffect(() => {
    if (enabled) {
      // Small delay to let initial data settle (e.g., from API load)
      const timer = setTimeout(() => {
        lastSavedDataRef.current = JSON.stringify(dataRef.current);
        if (mountedRef.current) {
          setIsDirty(false);
          autoSaveRegistry.markDirty(key, false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [enabled, key]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { lastSaved, saving, error, saveNow: doSave, isDirty };
}

// ============================================================================
// useBeforeUnload — Global unsaved changes warning
// ============================================================================

/** Call this once at the app level (e.g., in SecurityWrapper). */
export function useBeforeUnload(): void {
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (autoSaveRegistry.hasDirtySaves()) {
        e.preventDefault();
        // Modern browsers show a generic message
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
}

export default useAutoSave;
